/**
 * Declarative field configs of the two contract "fiche" cards (rendered by
 * the shared `EntityInfoCard`) and the mappers from the service view models
 * to their `values` (keyed by the same stable keys).
 *
 * - « Détails » (fiche 22803): `numero`, `produit`, `assureur`, `dateEffet`,
 *   `dateEcheance`, `primeAnnuelle` (€), `fractionnement` — the mobile order
 *   of the fiche.
 * - « Assuré » (fiche 22805): `nom`, `siret`, `adresse`, `email`,
 *   `telephone`.
 *
 * `null` values keep their slot (label shown, value empty).
 */

import type { ContractDetail, ContractInsured } from '../../api/contractsService';
import type { EntityInfoField, EntityInfoValue } from '../shared/EntityInfoCard';
import { formatEur } from '../../utils/format';

/** Stable keys of the contract details card. */
export type ContractDetailsFieldKey =
  | 'numero'
  | 'produit'
  | 'assureur'
  | 'dateEffet'
  | 'dateEcheance'
  | 'primeAnnuelle'
  | 'fractionnement';

/** Fields of `extranet.contractDetailsCard` (fiche order). */
export const CONTRACT_DETAILS_FIELDS: readonly EntityInfoField[] = [
  { key: 'numero', label: 'N° de contrat' },
  { key: 'produit', label: 'Produit' },
  { key: 'assureur', label: 'Assureur' },
  { key: 'dateEffet', label: "Date d'effet" },
  { key: 'dateEcheance', label: "Date d'échéance" },
  { key: 'primeAnnuelle', label: 'Prime annuelle', tooltip: 'Montant TTC en euros' },
  { key: 'fractionnement', label: 'Fractionnement' },
];

/** Stable keys of the contract insured card. */
export type ContractInsuredFieldKey = 'nom' | 'siret' | 'adresse' | 'email' | 'telephone';

/** Fields of `extranet.contractInsuredCard` (fiche order). */
export const CONTRACT_INSURED_FIELDS: readonly EntityInfoField[] = [
  { key: 'nom', label: 'Nom' },
  { key: 'siret', label: 'SIRET' },
  { key: 'adresse', label: 'Adresse' },
  { key: 'email', label: 'E-mail' },
  { key: 'telephone', label: 'Téléphone' },
];

/** Values of the details card; `null` when VEOS returned no record. */
export function contractDetailsValues(
  detail: ContractDetail | null | undefined,
): Record<ContractDetailsFieldKey, EntityInfoValue> | null {
  if (!detail) return null;
  const prime = formatEur(detail.primeAnnuelle);
  return {
    numero: detail.numero,
    produit: detail.produit,
    assureur: detail.assureur,
    dateEffet: detail.dateEffet,
    dateEcheance: detail.dateEcheance,
    primeAnnuelle: prime === '' ? null : prime,
    fractionnement: detail.fractionnement,
  };
}

/** Values of the insured card; `null` when VEOS returned no record. */
export function contractInsuredValues(
  insured: ContractInsured | null | undefined,
): Record<ContractInsuredFieldKey, EntityInfoValue> | null {
  if (!insured) return null;
  return {
    nom: insured.nom,
    siret: insured.siret,
    adresse: insured.adresse,
    email: insured.email,
    telephone: insured.telephone,
  };
}
